import { call, put, select, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import { AuthUser } from '@/core/classes/Auth'
import roomModel, { CreateRoomDto, Receiver, Room, UpdateRoomDto } from '@/core/models/room.model'

import { selectAuthUser, updateMyProfile as updateMyProfileAction } from '@/redux/reducers/auth.reducer'
import {
  addUserToRoom as addUserToRoomAction,
  addUserToRoomSuccess as addUserToRoomSuccessAction,
  addUserToRoomFailure as addUserToRoomFailureAction,
  createRoom as createRoomAction,
  createRoomSuccess as createRoomSuccessAction,
  createRoomFailure as createRoomFailureAction,
  getRoom as getRoomAction,
  getRoomSuccess as getRoomSuccessAction,
  getRoomFailure as getRoomFailureAction,
  updateRoom as updateRoomAction,
  updateRoomSuccess as updateRoomSuccessAction,
  updateRoomFailure as updateRoomFailureAction,
  sendEmergencyRoomInvitation as sendEmergencyRoomInvitationAction,
  sendEmergencyRoomInvitationSuccess as sendEmergencyRoomInvitationSuccessAction,
  sendEmergencyRoomInvitationFailure as sendEmergencyRoomInvitationFailureAction,
  emergencyRoomUpdatedSocketEvent,
  emergencyRoomActiveSocketEvent,
  emergencyRoomInactiveSocketEvent,
} from '@/redux/reducers/room.reducer'

import { updateMyProfile } from './auth.saga'

import { handleError } from '@/utils/errors'
import { CreateStaffDto } from '@/core/models/staff.model'
import { isStaff } from '@/utils/type-guards'

function* addUserToRoom(action: PayloadAction<string>) {
  try {
    const consultationId = action.payload
    const receiver: Receiver = yield call(roomModel.addUserToRoom, consultationId)
    yield put(addUserToRoomSuccessAction(receiver))
  } catch (exception) {
    console.warn(exception)
    yield put(addUserToRoomFailureAction(handleError(exception)))
  }
}

function* createRoom(action: PayloadAction<{ createRoomDto: CreateRoomDto; isEmergency?: boolean }>) {
  try {
    const { createRoomDto, isEmergency } = action.payload
    const room: Room = yield call(roomModel.createRoom, createRoomDto)
    yield put(createRoomSuccessAction(room))
    if (isEmergency) {
      yield call(updateMyProfile, updateMyProfileAction({ emergencyRoom: room._id }))
    }
  } catch (exception) {
    yield put(createRoomFailureAction(handleError(exception)))
  }
}

export function* getRoom(action: PayloadAction<string>) {
  try {
    const room: Room | null = yield call(roomModel.getRoom, action.payload)
    // GET /rooms/:id returns an empty body when no room exists for this id — routine for
    // group consultations opened before their shared room is created, or via a sibling
    // consultation the room is not linked to.
    if (!room) {
      yield put(getRoomFailureAction(handleError(new Error(`No room found for ${action.payload}`))))
      return
    }
    const roomCreator = typeof room.creator === 'string' ? room.creator : room.creator?._id
    const authUser: AuthUser = yield select(selectAuthUser)
    let peerId = null
    if (authUser?._id === roomCreator) peerId = room.streamer
    yield put(getRoomSuccessAction({ consultationId: typeof room.consultation === 'string' ? room.consultation : room.consultation?._id, peerId, room }))
  } catch (exception) {
    console.warn(exception)
    yield put(getRoomFailureAction(handleError(exception)))
  }
}

function* updateRoom(action: PayloadAction<{ roomId: string; updateRoomDto: UpdateRoomDto }>) {
  try {
    const { roomId, updateRoomDto } = action.payload
    const authUser: AuthUser | null = yield select(selectAuthUser)
    const room: Room = yield call(roomModel.updateRoom, roomId, updateRoomDto)
    yield put(updateRoomSuccessAction(room))
    if (isStaff(authUser) && !!authUser.emergencyRoom) {
      const emergencyRoomId = typeof authUser.emergencyRoom === 'string' ? authUser.emergencyRoom : authUser.emergencyRoom._id
      if (room._id === emergencyRoomId) yield put(emergencyRoomUpdatedSocketEvent({ staffId: authUser._id, roomId: emergencyRoomId }))
      if (room._id === emergencyRoomId && updateRoomDto.status === 'available') yield put(emergencyRoomActiveSocketEvent(emergencyRoomId))
      if (room._id === emergencyRoomId && updateRoomDto.status === 'inactive') yield put(emergencyRoomInactiveSocketEvent(emergencyRoomId))
    }
  } catch (exception) {
    yield put(updateRoomFailureAction(handleError(exception)))
  }
}

function* sendEmergencyRoomInvitation(action: PayloadAction<{ roomId: string; guest: CreateStaffDto }>) {
  try {
    const { roomId, guest } = action.payload
    yield call(roomModel.sendEmergencyRoomInvitation, roomId, guest)
    yield put(sendEmergencyRoomInvitationSuccessAction())
  } catch (exception) {
    yield put(sendEmergencyRoomInvitationFailureAction(handleError(exception)))
  }
}

const sagas = [
  takeLatest(addUserToRoomAction.type, addUserToRoom),
  takeLatest(createRoomAction.type, createRoom),
  takeLatest(getRoomAction.type, getRoom),
  takeLatest(updateRoomAction.type, updateRoom),
  takeLatest(sendEmergencyRoomInvitationAction.type, sendEmergencyRoomInvitation),
]

export default sagas
